import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'

import { Page, PageHeader } from '../../components/page'
import { T } from '../../copy/text'
import { t } from '../../copy'
import { useMe, useRedeemInvitation } from '../../app/queries'

/**
 * 邀请兑换页 `/team/invite?token=…`（闭环 2026-09-05）。
 *
 * 受邀人从链接进来：已登录就**自动兑换**（token 本身就是凭证，跟他用哪个
 * 邮箱注册的无关——先用别的邮箱注册过号的人，正是这条链要接住的人），
 * 成功告诉他「你的身份是什么」并送进今天屏。
 *
 * ## 没登录的那一半：带着 token 去登录，再带着 token 回来
 *
 * 2026-09-05 修的循环就在这儿：原来跳 /auth 不带回跳地址，登录完落 /lulu，
 * token 丢了；再点一次链接又来登录页——链接永远兑不了，人永远在登录页
 * 打转。现在去 /auth 时带 `return_to=/team/invite?token=…`，登录页登录完
 * 原样跳回来，自动兑换接上。
 */
export function InviteRedeemScreen() {
  const navigate = useNavigate()
  const me = useMe()
  const redeem = useRedeemInvitation()
  // 只自动兑换一次：失败重试由用户点按钮，不轮询轰炸
  const tried = useRef(false)
  const [result, setResult] = useState<'ok' | 'bad' | null>(null)
  const [roleName, setRoleName] = useState('')

  const search = useSearch({ strict: false }) as { token?: string }
  const token = (search.token ?? '').trim()
  const loggedIn = !me.isPending && !me.error

  useEffect(() => {
    if (!loggedIn || tried.current || !token) return
    tried.current = true
    redeem.mutate(token, {
      onSuccess: (member) => {
        setRoleName(member.role)
        setResult('ok')
      },
      onError: () => setResult('bad'),
    })
  }, [loggedIn, token, redeem])

  async function retry() {
    setResult(null)
    tried.current = true
    try {
      const member = await redeem.mutateAsync(token)
      setRoleName(member.role)
      setResult('ok')
    } catch {
      setResult('bad')
    }
  }

  return (
    <Page width="reading">
      <PageHeader title={<T id="team.invite.redeem.title" />} className="mb-npc-modal" />
      <div
        data-testid="invite-redeem"
        className="rounded-npc-card border border-npc-border-soft bg-npc-panel px-npc-card py-npc-card text-center"
      >
        {me.isPending ? (
          <p className="m-0 text-npc-body-sm text-npc-ink-soft"><T id="nav.common.loading" /></p>
        ) : !loggedIn ? (
          <div>
            <p className="m-0 mb-[12px] text-npc-body-sm text-npc-ink-soft">
              <T id="team.invite.redeem.need_login" />
            </p>
            <button
              type="button"
              onClick={() =>
                void navigate({
                  to: '/auth',
                  // as never：路由表没挂 validateSearch（挂了会搅浑全站 loose
                  // setSearch 的联合类型），这里自己保证形状。
                  search: { return_to: `/team/invite?token=${encodeURIComponent(token)}` } as never,
                })
              }
              className="h-[34px] cursor-pointer rounded-npc-button border-none bg-npc-green-deep px-[16px] font-[inherit] text-npc-caption font-medium text-white"
            >
              <T id="nav.auth" />
            </button>
          </div>
        ) : result === 'ok' ? (
          <div>
            <p className="m-0 mb-[14px] text-npc-body text-npc-ink-strong" data-testid="invite-redeem-ok">
              <T id="team.invite.redeem.joined" vars={{ role: roleName }} />
            </p>
            <button
              type="button"
              data-testid="invite-redeem-go"
              onClick={() => void navigate({ to: '/today' })}
              className="h-[34px] cursor-pointer rounded-npc-button border-none bg-npc-green-deep px-[16px] font-[inherit] text-npc-caption font-medium text-white"
            >
              <T id="team.invite.redeem.go" />
            </button>
          </div>
        ) : result === 'bad' ? (
          <div>
            <p role="alert" className="m-0 mb-[12px] text-npc-body-sm text-npc-amber-ink">
              <T id="team.invite.invalid" />
            </p>
            <button
              type="button"
              onClick={() => void retry()}
              className="h-[32px] cursor-pointer rounded-npc-chip border border-npc-border-soft bg-npc-panel px-[14px] font-[inherit] text-npc-body-sm text-npc-ink"
            >
              {t('error.read_failed.retry')}
            </button>
          </div>
        ) : (
          <p className="m-0 text-npc-body-sm text-npc-ink-soft"><T id="nav.common.loading" /></p>
        )}
      </div>
    </Page>
  )
}
